<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      ref="table"
      :custom-on-row-click="showCouponDetail"
      :filter="filterItem"
      :model="model"
      :use-action="false"
      :use-default-action="false"
      :use-select="false"
      entity-name="alreadySaledVouchers"
      icon="mdi-cards"
    >
      <template #footer="{tableItems}">
        <primary-button
          icon="mdi-text-search-variant"
          text="checkAll"
          @click="showAllList"
        />
        <primary-button
          icon="mdi-export"
          text="export"
          @click="exportFiles"
        />
        <v-card
          :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'"
          class="py-3 mr-4 text-body-2"
          color="grey lighten-3 black--text"
          dark
          outlined
        >
          {{ $t('TotalSales') }}：{{ tableItems.reduce((sum, it) => sum + parseFloat(it.totalAmount), 0) | priceDisplay }} |
          {{ $t('Surplus') }}：{{ tableItems.reduce((sum, it) => sum + parseFloat(it.leftAmount), 0) | priceDisplay }}
        </v-card>
      </template>
      <template
        #item.editVoucherCode="{item}"
      >
        <v-btn
          :height="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          :icon="!$vuetify.breakpoint.lgAndUp"
          :style="!$vuetify.breakpoint.lgAndUp ? 'border-radius: 12px; background: #f57c00;' : ''"
          :width="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          color="amber lighten-4 black--text"
          elevation="0"
          small
          @click.stop="getCurrentVoucher(item)"
        >
          <template v-if="!$vuetify.breakpoint.lgAndUp">
            <v-icon color="white">
              mdi-cog
            </v-icon>
          </template>
          <template v-if="$vuetify.breakpoint.lgAndUp">
            <div
              :class="!$vuetify.breakpoint.lgAndUp ? 'text-truncate' : ''"
              :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 100px;' : ''"
            >
              {{ $t('edit') }}
            </div>
          </template>
        </v-btn>
      </template>
    </ik-data-table>
    <nice-dialog v-model="showCouponDetailDialog">
      <v-container
        v-if="couponDetail"
        :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'"
        style="height: 98vh"
      >
        <page-header class="py-4 pb-6">
          {{ $t('vouchersUseRecord') }}
        </page-header>
        <div class="text-h5 font-weight-bold pl-2">
          {{ $t('voucherCode') }}: {{ couponDetail.longId }}
        </div>
        <span class="font-weight-regular pl-2 mt-2">{{ $t('value') }}: {{
          couponDetail.leftAmount | priceDisplay
        }}/{{ couponDetail.totalAmount | priceDisplay }}</span>

        <div class="text-body-1 pl-2">
          {{ $t('creatIn') }}: {{ couponDetail.createdAt | beautifulTime }}
        </div>
        <v-simple-table
          v-if="couponDetail.consumeLog.length>0"
          class="mt-4"
        >
          <thead>
            <tr>
              <th>{{ $t('orderNumber') }}</th>
              <th>{{ $t('time') }}</th>
              <th>{{ $t('useTheAmountOf') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="i in couponDetail.consumeLog"
              :key="i.id"
              @click="showOrder(i.orderId)"
            >
              <td>#{{ orderIdDisplay(i) }}</td>
              <td>{{ i.createdAt | beautifulTime }}</td>
              <td>{{ i.amount | priceDisplay }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <template v-else>
          <div class="pl-2 mt-4">
            {{ $t('Status') }}: {{ $t('noUsed') }}
          </div>
        </template>
      </v-container>
    </nice-dialog>
    <v-dialog
      v-model="editVoucherCodeDialog"
      max-width="600px"
    >
      <base-form
        :schema="schemas"
        @close="editVoucherCodeDialog=false"
        @submit="editVoucherCode"
      />
    </v-dialog>
    <v-dialog
      width="600px"
      v-model="showReportDialog"
    >
      <voucher-report-preview
        :loading="pdfDialogLoading"
        :date-range="currentDate"
        :sales-info="salesVoucherData"
        :used-info="usedVoucherData"
        :restaurant-info="restaurantInfo"
        @close="showReportDialog = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import MemberCardRecord, {
  editVoucherCode,
  getCouponRecord,
  getMemberCardListByDate
} from '@/model/dataLayer/api/bills/CouponSalesRecord'
import PageHeader from '@/views/widget/typography/PageHeader'
import NiceDialog from '@/views/widget/uiComponent/NiceDialog'
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import BaseForm from '@/views/widget/INK/BaseForm/BaseForm.vue'
import VoucherReportPreview from '@/views/AadenPages/voucher/components/VoucherReportPreview.vue'
import { getUsedMemberCardListByDate } from '@/model/dataLayer/api/bills/UsedVoucher'
import { getNiceRestaurantInfo } from '@/model/dataLayer/service/otherService'
import { getOrderDetail } from '@/model/dataLayer/api/bills/HistoryBills'

export default {
  name: 'VoucherStatistic',
  components: {
    BaseForm,
    PrimaryButton,
    PageHeader,
    NiceDialog,
    IkDataTable,
    VoucherReportPreview
  },
  data () {
    return {
      restaurantInfo: null,
      pdfDialogLoading: false,
      oldVoucher: {},
      editVoucherCodeDialog: false,
      showAll: true,
      model: MemberCardRecord,
      couponDetail: null,
      loading: false,
      showCouponDetailDialog: null,
      // report preview state
      showReportDialog: false,
      salesVoucherData: [],
      usedVoucherData: []
    }
  },
  props: {
    currentDate: {}
  },
  watch: {
    currentDate: {
      handler: function (val, oldVal) {
        if (oldVal) {
          this.showAll = false
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    schemas () {
      return [
        {
          key: 'longId',
          name: 'voucherCode',
          default: this.oldVoucher.longId ?? ''
        }
      ]
    },
    filterItem () {
      return { dateFilter: this.currentDate, showAll: this.showAll }
    }
  },
  methods: {
    orderIdDisplay (item) {
      if (item.returningOrderId) {
        return item.orderId + ' (Return#' + item.returningOrderId + ')'
      } else {
        return item.orderId
      }
    },
    async exportFiles () {
      this.pdfDialogLoading = true
      this.showReportDialog = true
      this.restaurantInfo = await getNiceRestaurantInfo()
      const [salesVoucher, usedVoucher] = await Promise.all([
        getMemberCardListByDate(this.currentDate),
        getUsedMemberCardListByDate(this.currentDate)
      ])
      console.log(salesVoucher, 'salesVoucher')
      console.log(usedVoucher, 'usedVoucher')
      // Save data to state and open the preview dialog
      this.salesVoucherData = salesVoucher
      this.usedVoucherData = usedVoucher
      this.pdfDialogLoading = false
    },
    getCurrentVoucher (item) {
      this.oldVoucher = item
      this.editVoucherCodeDialog = true
    },
    async editVoucherCode (info) {
      await editVoucherCode(this.oldVoucher.longId, info.longId)
      this.editVoucherCodeDialog = false
      await this.$refs.table.reload()
    },
    showAllList () {
      this.showAll = true
    },
    async showCouponDetail (coupon) {
      const res = (await getCouponRecord(coupon.id))
      for (const it of res.consumeLog) {
        it.returningOrderId = (await getOrderDetail(it.orderId)).returningOrderId ?? ''
        if (it.returningOrderId) {
          it.amount = Math.abs(it.amount)
        }
      }
      this.couponDetail = res
      this.showCouponDetailDialog = true
    }
  }
}
</script>

<style scoped>

</style>
